import {resourceSuppliers} from '../constants';
import {BITBUCKET_BUILD_NUMBER} from '../deployVersion';
import addUploadFeature from './addUploadFeature';
import backendRoutesConfig from './backendRoutesConfig';
import {fetchJson, queryParameters} from './fetch';
import {
    CREATE,
    DELETE,
    DELETE_MANY,
    FETCH,
    GET,
    GET_LIST,
    GET_MANY,
    GET_MANY_REFERENCE,
    GET_ONE,
    UPDATE,
    UPDATE_MANY,
} from './types';

const getUrlForRequest = (config, resource, type, params) => {
    // if there is no overrides for entire resource
    if (!config.overrides[resource]) {
        return config.defaultApiUrl;
    }
    // if there is no overrides for type of request
    if (!config.overrides[resource][type]) {
        return config.defaultApiUrl;
    }
    // if there is no override for custom endpoint
    if (!config.overrides[resource][type].forAll &&
        !config.overrides[resource][type].endpoints.includes(params['_endpoint'])) {
        return config.defaultApiUrl;
    }
    return config.newApiUrls[config.overrides[resource][type].apiUrlIdentifier];
};

const checkVersion = (webVersion, serverVersion) => {
    if (Number(webVersion) < Number(serverVersion)) {
        setTimeout(() => {
            window.location.reload();
        }, 3 * 60 * 1000); // 3 min
    }
};

/**
 * Maps admin-on-rest queries to a loopback powered REST API
 *
 * @see https://github.com/strongloop/loopback
 * @example
 * GET_LIST     => GET http://my.api.url/posts?filter[sort]="title ASC"&filter[skip]=0&filter[limit]=20
 * GET_ONE      => GET http://my.api.url/posts/123
 * GET_MANY     => GET http://my.api.url/posts?filter[where][or]=[{id:123},{id:456}]
 * UPDATE       => PATCH http://my.api.url/posts/123
 * CREATE       => POST http://my.api.url/posts/123
 * DELETE       => DELETE http://my.api.url/posts/123
 */
const fetchProvider = (config, httpClient = fetchJson) => {
    /**
     * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
     * @param {String} resource Name of the resource to fetch, e.g. 'posts'
     * @param {Object} params The REST request params, depending on the type
     * @returns {Object} { url, options } The HTTP request parameters
     */
    const convertRESTRequestToHTTP = (type, resource, params) => {
        let url = '';
        const options = {};
        const query = {};
        const queryParams = {
            filter: query,
        };

        const apiUrl = getUrlForRequest(config, resource, type, params);

        switch (type) {
            case GET: {
                Object.keys(params).forEach(key => {
                    queryParams[key] = params[key];
                });

                url = `${apiUrl}/${resource}`;
                break;
            }
            case GET_LIST: {
                const {filter, fields, pagination, include, sort, ...restParams} = params;

                query.where = {...filter};
                query.fields = {...fields};

                if (sort) {
                    const {field, order} = sort;
                    query.order = [field + ' ' + order];
                }

                if (pagination) {
                    const {page, perPage} = pagination;
                    if (perPage > 0) {
                        query.limit = perPage;
                        if (page >= 0) {
                            query.skip = (page - 1) * perPage;
                        }
                    }
                }

                if (typeof include === 'string') {
                    query.include = include;
                } else if (Array.isArray(include)) {
                    query.include = include;
                } else if (params.include) {
                    query.include = {...include};
                }

                for (const param in restParams) {
                    query[param] = restParams[param];
                }

                url = `${apiUrl}/${resource}`;
                break;
            }
            case GET_ONE:
                let include;
                if (typeof params.include === 'string') {
                    include = params.include;
                } else if (Array.isArray(params.include)) {
                    include = params.include;
                } else if (params.include) {
                    include = {...params.include};
                }
                url = `${apiUrl}/${resource}/${params.id}`;
                query.include = include;
                break;
            case GET_MANY: {
                let sendingParam = 'id';
                if (resource === resourceSuppliers) {
                    sendingParam = 'name';
                }
                const listId = params.ids.map(id => {
                    return {[sendingParam]: id};
                });
                query.where = {or: listId};

                url = `${apiUrl}/${resource}`;
                break;
            }
            case GET_MANY_REFERENCE: {
                const {page, perPage} = params.pagination;
                const {field, order} = params.sort;
                query['where'] = {...params.filter};
                query['where'][params.target] = params.id;
                if (field) {
                    query['order'] = [field + ' ' + order];
                }
                if (perPage > 0) {
                    query['limit'] = perPage;
                    if (page >= 0) {
                        query['skip'] = (page - 1) * perPage;
                    }
                }
                url = `${apiUrl}/${resource}`;
                break;
            }
            case UPDATE:
                url = `${apiUrl}/${resource}/${params.id}`;
                options.method = 'PATCH';
                options.body = JSON.stringify(params.data);
                break;
            case UPDATE_MANY:
                url = `${apiUrl}/${resource}/update`;
                options.method = 'POST';
                options.body = JSON.stringify(params.data);
                queryParams.where = params.where;
                break;
            case CREATE:
                url = `${apiUrl}/${resource}`;
                options.method = 'POST';
                options.body = JSON.stringify(params.data);
                break;
            case FETCH:
                if (params._endpoint) {
                    resource = `${resource}/${params._endpoint}`;
                    delete params._endpoint;
                }

                url = `${apiUrl}/${resource}`;
                options.method = 'POST';
                options.body = JSON.stringify(params);
                break;
            case DELETE:
                url = `${apiUrl}/${resource}/${params.id}`;
                options.method = 'DELETE';
                break;
            default:
                throw new Error(`Unsupported fetch action type!!! ${type}`);
        }

        Object.keys(queryParams).forEach(key => {
            if (typeof queryParams[key] === 'object') {
                queryParams[key] = JSON.stringify(queryParams[key]);
            } else {
                queryParams[key] = queryParams[key];
            }
        });
        url = `${url}?${queryParameters(queryParams)}`;

        return {url, options};
    };

    /**
     * @param {Object} response HTTP response from fetch()
     * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
     * @param {String} resource Name of the resource to fetch, e.g. 'posts'
     * @param {Object} params The REST request params, depending on the type
     * @returns {{data: object | {data: object | Array<object>}}} REST response
     */
    const convertHTTPResponseToREST = (response, type, resource, params) => {
        const {headers, json} = response;

        const serverVersion = headers.get('x-build-version');
        checkVersion(BITBUCKET_BUILD_NUMBER, serverVersion);

        switch (type) {
            case GET_LIST:
            case GET_MANY_REFERENCE:
                if (!headers.has('x-total-count')) {
                    throw new Error(
                        `The X-Total-Count header is missing in the HTTP Response. The jsonServer REST client expects
                        responses for lists of resources to contain this header with the total number of results
                        to build the pagination. If you are using CORS, did you declare X-Total-Count
                        in the Access-Control-Expose-Headers header?`,
                    );
                }
                return {
                    data: json,
                    total: parseInt(
                        headers
                            .get('x-total-count')
                            .split('/')
                            .pop(),
                        10,
                    ),
                };
            case CREATE:
                return {data: {...params.data, id: json.id}};
            case FETCH:
                return {data: json};
            case DELETE:
                return {data: {...params.data, id: params.id}};
            case GET_ONE:
                return {data: json, serverVersion};
            default:
                return {data: json};
        }
    };

    /**
     * @param {string} type Request type, e.g GET_LIST
     * @param {string} resource Resource name, e.g. "posts"
     * @param {Object} payload Request parameters. Depends on the request type
     * @returns {Promise<{data: object | {data: object | Array<object>}}>} the Promise for a REST response
     */
    return (type, resource, params) => {
        const apiUrl = getUrlForRequest(config, resource, type, params);

        if (type === DELETE_MANY) {
            return Promise.all(
                params.ids.map(id =>
                    httpClient(`${apiUrl}/${resource}/${id}`, {
                        method: 'DELETE',
                    }),
                ),
            ).then(responses => ({
                data: responses.map(response => response.json),
            }));
        }

        const {url, options} = convertRESTRequestToHTTP(type, resource, params);
        return httpClient(url, options).then(response =>
            convertHTTPResponseToREST(response, type, resource, params),
        );
    };
};

/**
 *
 * @param {string} type
 * @param {string} resource
 * @param {any} params
 * @return {Promise<DataProviderResponseDataShape>}
 */
export const simpleDataProvider = (type, resource, params) => {
    const {type: newType, resource: newResource, params: newParams} = addUploadFeature(type, resource, params);

    return fetchProvider(backendRoutesConfig)(newType, newResource, newParams);
};
